function initTimelineSection(section) {
    const slides = Array.from(section.querySelectorAll(".timeline__slide"));
    const contents = Array.from(section.querySelectorAll(".timeline__content"));
    const points = Array.from(section.querySelectorAll(".timeline__point"));
    const track = section.querySelector(".timeline__track");
    const trackWrap = section.querySelector(".timeline__track-wrap");
    const fill = section.querySelector("[data-track-fill]");
    const pauseButton = section.querySelector("[data-timeline-pause]");

    if (!slides.length || !points.length) {
        return;
    }

    const interval = parseInt(section.dataset.interval, 10) || 5500;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let activeIndex = 0;
    let timer = null;
    let isPaused = reducedMotion;
    // Durata rimasta nel ciclo corrente e istante in cui è ripartito: permettono
    // a pause()/play() di fermare la barra esattamente dov'è e farla ripartire
    // dallo stesso punto, invece di riavviare un intervallo pieno.
    let cycleDuration = interval;
    let cycleStartedAt = 0;

    // Le slide oltre la prima arrivano dal server senza background-image (vedi timeline.php):
    // lo si applica solo quando la slide sta per diventare attiva, per non scaricare tutte
    // le immagini a piena risoluzione fin dal primo render.
    function ensureSlideBg(index) {
        const slide = slides[index];

        if (slide && slide.dataset.bg) {
            slide.setAttribute("style", slide.dataset.bg);
            delete slide.dataset.bg;
        }
    }

    function pointCenter(index) {
        const trackRect = track.getBoundingClientRect();
        const pointRect = points[index].getBoundingClientRect();

        return pointRect.left - trackRect.left + pointRect.width / 2;
    }

    // Ferma la barra sul punto corrente senza transizione (nessuna animazione in corso).
    function placeFillAt(index) {
        if (!track || !fill) {
            return;
        }

        fill.style.transition = "none";
        fill.style.width = `${pointCenter(index)}px`;
        void fill.offsetWidth;
    }

    // Fa "caricare" la barra in tempo reale verso il punto successivo per tutta
    // la durata passata: così l'avanzamento è visibile continuamente, non solo
    // al cambio slide. `duration` può essere l'intervallo pieno o, dopo una
    // pausa, solo il tempo rimasto (vedi resumeCycle).
    function animateFillToNext(duration) {
        if (!track || !fill) {
            return;
        }

        const nextIndex = (activeIndex + 1) % slides.length;
        ensureSlideBg(nextIndex);

        fill.style.transition = `width ${duration}ms linear`;
        fill.style.width = `${pointCenter(nextIndex)}px`;
    }

    // Blocca la barra esattamente alla larghezza renderizzata in questo istante,
    // interrompendo la transizione CSS in corso senza farla scattare al valore finale.
    function freezeFill() {
        if (!fill) {
            return;
        }

        const currentWidth = fill.getBoundingClientRect().width;
        fill.style.transition = "none";
        fill.style.width = `${currentWidth}px`;
        void fill.offsetWidth;
    }

    // Su mobile/tablet la track ha più anni di quanti ne stiano a vista (scroll
    // orizzontale, vedi _timeline.scss). Finché il punto attivo è già dentro
    // alla finestra visibile (i primi 4-5 anni mostrati di default) la track
    // resta ferma; scorre solo quando le slide avanzano oltre gli anni a vista.
    // Il riavvolgimento (ultimo anno -> primo) riporta sempre la vista
    // all'inizio, nello stesso istante in cui si resetta anche la barra di
    // caricamento (il jump immediato in placeFillAt, dentro startCycle):
    // stesso goTo, stesso tick, animazioni sincronizzate.
    function scrollTrackToActive() {
        if (!trackWrap) {
            return;
        }

        if (activeIndex === 0) {
            trackWrap.scrollTo({ left: 0, behavior: reducedMotion ? "auto" : "smooth" });
            return;
        }

        const point = points[activeIndex];

        if (!point) {
            return;
        }

        const wrapRect = trackWrap.getBoundingClientRect();
        const pointRect = point.getBoundingClientRect();

        if (pointRect.left >= wrapRect.left && pointRect.right <= wrapRect.right) {
            return;
        }

        const offset = pointRect.left - wrapRect.left + pointRect.width / 2 - wrapRect.width / 2;

        trackWrap.scrollTo({
            left: trackWrap.scrollLeft + offset,
            behavior: reducedMotion ? "auto" : "smooth",
        });
    }

    function goTo(index) {
        activeIndex = (index + slides.length) % slides.length;
        ensureSlideBg(activeIndex);

        slides.forEach((slide, i) => slide.classList.toggle("is-active", i === activeIndex));
        contents.forEach((content, i) => content.classList.toggle("is-active", i === activeIndex));
        points.forEach((point, i) => {
            const isActive = i === activeIndex;
            point.classList.toggle("is-active", isActive);
            point.setAttribute("aria-selected", isActive ? "true" : "false");
            // Roving tabindex (pattern ARIA tablist): un solo tab è raggiungibile
            // con Tab, gli altri si raggiungono con le frecce (vedi keydown sotto).
            point.setAttribute("tabindex", isActive ? "0" : "-1");
        });
        scrollTrackToActive();
        startCycle(interval);
    }

    function stopTimer() {
        if (timer) {
            window.clearTimeout(timer);
            timer = null;
        }
    }

    // Avvia un ciclo di `duration` ms per la slide attiva: posiziona la barra sul
    // punto corrente e, se non in pausa/reduced-motion, la fa avanzare verso il
    // prossimo punto e pianifica il cambio slide allo scadere del tempo.
    function startCycle(duration) {
        stopTimer();
        cycleDuration = duration;
        placeFillAt(activeIndex);

        if (isPaused || reducedMotion) {
            return;
        }

        animateFillToNext(duration);
        cycleStartedAt = Date.now();
        timer = window.setTimeout(() => goTo(activeIndex + 1), duration);
    }

    // Riprende il ciclo interrotto da pause() per il tempo rimasto, mantenendo
    // la stessa velocità di avanzamento (non un intervallo pieno da capo).
    function resumeCycle() {
        if (cycleDuration <= 0) {
            goTo(activeIndex + 1);
            return;
        }

        animateFillToNext(cycleDuration);
        cycleStartedAt = Date.now();
        timer = window.setTimeout(() => goTo(activeIndex + 1), cycleDuration);
    }

    // Controllo esplicito di pausa (WCAG 2.2.2): ferma la barra esattamente dov'è
    // e il cambio slide, senza far ripartire un intervallo pieno alla ripresa.
    function updatePauseButton() {
        if (!pauseButton) {
            return;
        }

        pauseButton.classList.toggle("is-paused", isPaused);
        pauseButton.setAttribute(
            "aria-label",
            isPaused ? "Riprendi la timeline" : "Metti in pausa la timeline"
        );
    }

    function pause() {
        if (isPaused) {
            return;
        }

        isPaused = true;
        cycleDuration = Math.max(cycleDuration - (Date.now() - cycleStartedAt), 0);
        freezeFill();
        stopTimer();
        updatePauseButton();
    }

    function play() {
        if (reducedMotion || !isPaused) {
            return;
        }

        isPaused = false;
        resumeCycle();
        updatePauseButton();
    }

    if (pauseButton) {
        pauseButton.addEventListener("click", () => {
            if (isPaused) {
                play();
            } else {
                pause();
            }
        });
    }

    points.forEach((point, i) => {
        point.addEventListener("click", () => goTo(i));
    });

    // Frecce/Home/End per muoversi tra i tab (ARIA Authoring Practices, pattern
    // Tabs con attivazione automatica): lo spostamento del focus attiva subito
    // la slide corrispondente, come un vero tablist.
    if (track) {
        track.addEventListener("keydown", (event) => {
            const { key } = event;

            if (key === "ArrowLeft") {
                event.preventDefault();
                goTo(activeIndex - 1);
            } else if (key === "ArrowRight") {
                event.preventDefault();
                goTo(activeIndex + 1);
            } else if (key === "Home") {
                event.preventDefault();
                goTo(0);
            } else if (key === "End") {
                event.preventDefault();
                goTo(slides.length - 1);
            } else {
                return;
            }

            points[activeIndex].focus();
        });
    }

    window.addEventListener("resize", () => {
        if (!fill) {
            return;
        }

        fill.style.transition = "none";
        fill.style.width = `${pointCenter(activeIndex)}px`;
    });

    goTo(0);
    updatePauseButton();
}

export function initTimeline() {
    document.querySelectorAll("[data-timeline]").forEach(initTimelineSection);
}
